<template>
    <div v-show="isCookieBarShown && !isCookieManaged" class="cookiebar-overlay"></div>
    <div v-show="isCookieBarShown" v-if="isCookieBarShown && showCookie" class="cookiebar footer__cookie">
        <div class="cookiebar__content">
            <div class="cookiebar__title mb-1">{{ $t('cookiebar.title') }}</div>
            <div class="cookiebar__text mb-3">
                <div class="inner">
                    {{ $t('cookiebar.text') }}
                </div>
            </div>

            <div class="cookiebar__actions">
                <button class="btn btn-primary rounded-pill mt-2 mt-sm-0" data-cy="accept-cookies" @click="changeAllCookies('granted')">
                    {{ $t('cookiebar.acceptText') }}
                </button>

                <div class="secondary-btns">
                    <a class="link" :href="$t('cookiebar.manageLink')" @click="manageCookies()">
                        {{ $t('cookiebar.manageText') }}
                    </a>

                    <a class="link" @click.prevent="changeAllCookies('denied')">
                        {{ $t('cookiebar.denyText') }}
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { useCookie } from 'nuxt/app'
import DataLayer from '~/extensions/DataLayer'

const AD_STORAGE = 'ad_storage'
const ANALYTICS_STORAGE = 'analytics_storage'
const FUNCTIONALITY_STORAGE = 'functionality_storage'
const AD_USER_DATA = 'ad_user_data'
const AD_PERSONALIZATION = 'ad_personalization'

export default {
    props: {
        showCookie: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        const grantedSettings = {
            [AD_STORAGE]: 'granted',
            [ANALYTICS_STORAGE]: 'granted',
            [FUNCTIONALITY_STORAGE]: 'granted',
            [AD_USER_DATA]: 'granted',
            [AD_PERSONALIZATION]: 'granted',
        }
        const deniedSettings = {
            [AD_STORAGE]: 'denied',
            [ANALYTICS_STORAGE]: 'denied',
            [FUNCTIONALITY_STORAGE]: 'denied',
            [AD_USER_DATA]: 'denied',
            [AD_PERSONALIZATION]: 'denied',
        }
        const defaultSettings = deniedSettings

        return {
            isCookieBarShown: false,
            isCookieManaged: false,
            cookieSettings: defaultSettings,
            defaultSettings,
            grantedSettings,
            deniedSettings,
        }
    },

    mounted(): void {
        // const showCookiesBarFromCookies = useShowCookiesBarCookie().value
        // this.isCookieBarShown = showCookiesBarFromCookies === undefined ? true : showCookiesBarFromCookies
        this.cookieSettings = this.getCookieSettings() || this.defaultSettings
        this.checkCookieBarStatus()
        this.initCookieCheckboxEvents()
        this.updateCheckboxStatus()
        this.pushToGtm('default')

        this.isCookieManaged = this.getCookiesManagementSettings()
    },

    updated(): void {
        this.checkCookieBarStatus()
    },

    methods: {
        initCookieCheckboxEvents(): void {
            const checkboxes = [...document.getElementsByClassName('js-cookies-checkbox')] as HTMLInputElement[]

            checkboxes.forEach((checkbox: HTMLInputElement) => {
                checkbox.addEventListener('change', () => {
                    if (checkbox.id === AD_STORAGE) {
                        this.changeSingleCookieSetting({
                            cookieName: AD_USER_DATA,
                            isGranted: checkbox.checked,
                        })
                        this.changeSingleCookieSetting({
                            cookieName: AD_PERSONALIZATION,
                            isGranted: checkbox.checked,
                        })
                    }
                    this.changeSingleCookieSetting({
                        cookieName: checkbox.id,
                        isGranted: checkbox.checked,
                    })

                    this.saveCookieSettings(this.cookieSettings)
                })
            })
        },

        checkCookieBarStatus(): void {
            this.isCookieBarShown = this.getCookieSettings() === null && typeof localStorage !== 'undefined'
            useCookie('showCookiesBar').value = this.isCookieBarShown
        },

        changeAllCookies(status: string): void {
            setTimeout(() => {
                this.isCookieBarShown = false
                const cookieSettings = {
                    [AD_STORAGE]: status,
                    [ANALYTICS_STORAGE]: status,
                    [FUNCTIONALITY_STORAGE]: status,
                    [AD_USER_DATA]: status,
                    [AD_PERSONALIZATION]: status,
                }

                this.saveCookieSettings(cookieSettings)
            }, 100)
        },

        changeSingleCookieSetting({ cookieName, isGranted }: { cookieName: string; isGranted: boolean }): void {
            this.cookieSettings[cookieName] = isGranted ? 'granted' : 'denied'
        },

        saveCookieSettings(cookieSettings: object): void {
            cookieSettings = JSON.parse(JSON.stringify(cookieSettings))

            this.isCookieBarShown = false
            localStorage.setItem('pilulkaCookieSettings', JSON.stringify(cookieSettings))
            useCookieSettingsCookie().value = cookieSettings

            Object.entries(cookieSettings).forEach(([cookieName, status]) => {
                this.$repository.logConsentChange(cookieName, status, useUidCookie().value)
            })

            this.cookieSettings = cookieSettings
            this.pushToGtm('update')
        },

        pushToGtm(eventType: 'update' | 'default') {
            // @ts-ignore
            DataLayer.gtag('consent', eventType, this.cookieSettings)
            if (eventType === 'update') {
                DataLayer.push({
                    event: 'consent_update',
                })
            }
        },

        validateCookieSetting(cookieSettings: { [name: string]: string }, name: string): void {
            const isCookieSettingValid = ['granted', 'denied'].includes(cookieSettings[name])

            if (!isCookieSettingValid) {
                throw new TypeError('Cookie setting is not granted nor denied and thus is invalid.')
            }
        },

        getCookieSettings(): object | null {
            try {
                const localStorageData = localStorage.getItem('pilulkaCookieSettings')
                if (!localStorageData) {
                    return null
                }

                const cookieSettings = JSON.parse(localStorageData)
                this.validateCookieSetting(cookieSettings, AD_STORAGE)
                this.validateCookieSetting(cookieSettings, ANALYTICS_STORAGE)
                this.validateCookieSetting(cookieSettings, FUNCTIONALITY_STORAGE)
                this.validateCookieSetting(cookieSettings, AD_USER_DATA)
                this.validateCookieSetting(cookieSettings, AD_PERSONALIZATION)

                if (!useCookieSettingsCookie().value) {
                    useCookieSettingsCookie().value = cookieSettings
                }
                return cookieSettings
            } catch {
                return null
            }
        },

        manageCookies() {
            this.cookieManaged = false
            localStorage.setItem('pilulkaCookiesManagement', '1')
        },

        getCookiesManagementSettings() {
            const localStorageData = localStorage.getItem('pilulkaCookiesManagement')
            if (!localStorageData) {
                return false
            }

            return localStorageData
        },

        updateCheckboxStatus() {
            const cookieSettings = this.getCookieSettings() || this.defaultSettings

            ;[AD_STORAGE, ANALYTICS_STORAGE, FUNCTIONALITY_STORAGE].forEach((id) => {
                const checkbox = document.getElementById(id) as HTMLInputElement

                if (checkbox) {
                    checkbox.checked = cookieSettings[id] === 'granted'
                }
            })
        },
    },
}
</script>

<style lang="scss" scoped>
.cookiebar-overlay {
    position: fixed;
    z-index: 9999;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);

    @media (min-width: 480px) {
        display: none;
    }
}

.cookiebar {
    position: fixed;
    z-index: 10000;
    bottom: 0;
    left: 0;
    background: $color-white;
    padding: 1em;
    padding-bottom: 0;
    box-shadow: 0 -7px 17px -8px #00000040;
    font-size: 14px;
    width: 100%;
    margin: auto;

    @media (min-width: 480px) {
        padding: 70px 1em 80px 1em;
    }

    &__content {
        max-width: 1280px;
        margin: auto;
    }

    &__title {
        font-size: 18px;
        padding: 0 0 20px 0;
        font-weight: 590;
        span {
            font-size: 13px;
        }
    }

    &__text {
        display: flex;
        overflow-y: auto;
        font-size: 14px;
        line-height: 16px;
        position: relative;
    }

    &__actions {
        font-size: 13px;

        .secondary-btns {
            display: flex;
            justify-content: space-between;

            @media (min-width: 480px) {
                flex-direction: row-reverse;
            }
        }

        .link {
            color: #424242;
            text-decoration: underline;
            display: inline-block;
            padding: 1em 0;
            text-align: center;
            cursor: pointer;
            &:hover {
                text-decoration: none;
            }
            @media (min-width: 480px) {
                margin-right: 3rem;
            }
        }

        @media (min-width: 480px) {
            display: flex;
            align-items: center;
        }
    }

    .btn {
        width: 100%;
        font-size: 13px;
        // sadly screen.css overrides border-radius, therefore important has to be used here
        border-radius: 50px !important;

        &:hover {
            background: $color-cta-dark;
        }

        @media (min-width: 480px) {
            margin-right: 3rem;
            max-width: 380px;
        }
    }
}
</style>
